import type { LevelTableColumn } from 'redesign';
import { ContextMenuButton, TableCell, TableHeader } from 'redesign';
import { IconFlexPoolOnlyJob, IconWarning } from 'assets/img';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ClaimantCellProps,
  ClaimantColumnDefinition,
  ClaimantsTableOptions,
} from './claimant-table.types';
import { useGetShiftDetailsColumnDefinitionBase } from '../../shared/columns/use-get-shift-details-column-definition-base';

export const useGetClaimantColumnDefinition: (options?: {
  hideEmploymentTypeFields?: boolean;
}) => LevelTableColumn<ClaimantColumnDefinition, ClaimantsTableOptions>[] = ({
  hideEmploymentTypeFields = false,
} = {}) => {
  const [flexWorkerImage, fw] = useGetShiftDetailsColumnDefinitionBase();
  const { t } = useTranslation('hq-screen-specific');

  const flexPoolIndicator: LevelTableColumn<ClaimantColumnDefinition, ClaimantsTableOptions> = {
    Header: '',
    accessor: 'flexPoolIndicator',
    Cell: FlexPoolIndicatorCell,
  };
  const email: LevelTableColumn<ClaimantColumnDefinition, ClaimantsTableOptions> = {
    Header: () => (
      <TableHeader>
        {t('shifts.placements.table_headers.email', { ns: 'hq-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'email',
    Cell: EmailCell,
  };

  const phone: LevelTableColumn<ClaimantColumnDefinition, ClaimantsTableOptions> = {
    Header: () => (
      <TableHeader>
        {t('shifts.placements.table_headers.phone', { ns: 'hq-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'phone',
    Cell: PhoneCell,
  };

  const employmentTypeMismatch: LevelTableColumn<ClaimantColumnDefinition, ClaimantsTableOptions> =
    {
      Header: '',
      accessor: 'employmentTypeMismatch',
      Cell: EmploymentTypeMismatchCell,
    };

  const actions: LevelTableColumn<ClaimantColumnDefinition, ClaimantsTableOptions> = {
    accessor: 'actions',
    Header: '',
    Cell: ActionCell,
  };

  const noShow: LevelTableColumn<ClaimantColumnDefinition, ClaimantsTableOptions> = {
    accessor: 'noShow',
    Header: '',
    Cell: NoShowCell,
  };

  return [
    flexWorkerImage,
    flexPoolIndicator,
    fw,
    email,
    phone,
    ...(hideEmploymentTypeFields ? [] : [employmentTypeMismatch]),
    noShow,
    actions,
  ];
};

const FlexPoolIndicatorCell = ({ value }: ClaimantCellProps<'flexPoolIndicator'>) => {
  return value ? <IconFlexPoolOnlyJob width={24} /> : null;
};

const EmailCell = ({ value }: ClaimantCellProps<'email'>) => {
  return <TableCell>{value}</TableCell>;
};

const PhoneCell = ({ value }: ClaimantCellProps<'phone'>) => {
  return <TableCell>{value}</TableCell>;
};

const NoShowCell = ({ value }: ClaimantCellProps<'noShow'>) => {
  if (value) {
    return (
      <Typography variant="body1" color="secondary">
        No-show
      </Typography>
    );
  }
  return <></>;
};

const EmploymentTypeMismatchCell = ({ value }: ClaimantCellProps<'employmentTypeMismatch'>) => {
  const { t } = useTranslation('hq-screen-specific');

  if (value) {
    return (
      <Tooltip
        title={t('shifts.placements.employment_type_not_supported', {
          ns: 'hq-screen-specific',
        })}
      >
        <IconBox data-testid="employment-type-mismatch-warning">
          <StyledIcon as={IconWarning} />
        </IconBox>
      </Tooltip>
    );
  }
  return <></>;
};

const ActionCell = ({ value, getRowActions }: ClaimantCellProps<'actions'>) => {
  const actions = getRowActions(value);

  if (actions.length === 0) {
    return null;
  }

  return (
    <ContextMenuButton menuId={`claimant-actions-menu-${value.workerId}`}>
      {actions}
    </ContextMenuButton>
  );
};

const StyledIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwDanger[100],
    strokeWidth: theme.spacing(0.5),
  },
}));

const IconBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));
